import { gql } from '@apollo/client';
import React from 'react';
import FavoriteIcon from '../components/FavoriteIcon';
import { MenuItem } from '../components/Menu';
import OverflowMenu from '../components/OverflowMenu';
import SVGIcon from '../components/SVGIcon';
import type { Favorites_PostFragment } from '../graphql/generated/graphqlTypes';
import overflowMenuIcon from '../icons/overflowMenu.svg';
import reorderIcon from '../icons/reorder.svg';
import type { AllTopicsTreeListTopic } from '../topic/useAllTopics';
import UIActions from '../ui/UIActions';
import styles from './Favorites.module.scss';
import ReorderFavoritesModal from './ReorderFavoritesModal';
import SidebarItems from './SidebarItems';
import SidebarSection from './SidebarSection';
import sortSidebarItems from './sortSidebarItems';
import useSidebarSettings from './useSidebarSettings';
import { useAppDispatch } from '../app/redux/useAppSelector';

const POST_FRAGMENT = gql`
  fragment Favorites_post on Post {
    id
    ...SidebarItems_post
  }
  ${SidebarItems.fragments.post}
`;

interface FavoritesProps {
  activeTopics: Record<string, boolean>;
  expandedTopics: Record<string, boolean>;
  favoritePosts: Favorites_PostFragment[];
  favoriteTopics: AllTopicsTreeListTopic[];
}

const Favorites = ({
  activeTopics,
  expandedTopics,
  favoritePosts,
  favoriteTopics,
}: FavoritesProps) => {
  const favoriteItemsCount = favoritePosts.length + favoriteTopics.length;

  const { favoriteIds } = useSidebarSettings();

  const dispatch = useAppDispatch();

  if (favoriteItemsCount === 0) {
    return null;
  }

  const stopEvent = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <SidebarSection
      name="favorites"
      icon={<FavoriteIcon active />}
      title="Favorites"
      hoverContent={
        favoriteTopics.length + favoritePosts.length > 1 && (
          // eslint-disable-next-line jsx-a11y/no-static-element-interactions -- TODO - fix next time this file is edited
          <div className={styles.hoverContent} onClick={stopEvent}>
            <OverflowMenu
              button={
                <div className={styles.menuButton}>
                  <SVGIcon size={16} coloringWith="fill">
                    {overflowMenuIcon}
                  </SVGIcon>
                </div>
              }
            >
              <MenuItem
                icon={reorderIcon}
                onClick={() => {
                  dispatch(
                    UIActions.openModal(ReorderFavoritesModal, {
                      favorites: sortSidebarItems(
                        [...favoriteTopics, ...favoritePosts],
                        favoriteIds,
                      ),
                    }),
                  );
                }}
              >
                Reorder favorites
              </MenuItem>
            </OverflowMenu>
          </div>
        )
      }
    >
      <SidebarItems
        activeTopics={activeTopics}
        expandedTopics={expandedTopics}
        showIcon
        posts={favoritePosts}
        topics={favoriteTopics}
        orderIds={favoriteIds}
      />
    </SidebarSection>
  );
};

Favorites.fragments = {
  post: POST_FRAGMENT,
};

export default Favorites;
